"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";

const SIDEBAR_VISIBILITY_STORAGE_KEY = "geooffice-admin-sidebar-collapsed";
const SIDEBAR_VISIBILITY_EVENT = "geooffice-admin-sidebar-change";

type SidebarVisibilityContextValue = {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
};

const SidebarVisibilityContext =
  createContext<SidebarVisibilityContextValue | null>(null);

export function SidebarVisibilityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const subscribe = useCallback((onStoreChange: () => void) => {
    window.addEventListener("storage", onStoreChange);
    window.addEventListener(SIDEBAR_VISIBILITY_EVENT, onStoreChange);

    return () => {
      window.removeEventListener("storage", onStoreChange);
      window.removeEventListener(SIDEBAR_VISIBILITY_EVENT, onStoreChange);
    };
  }, []);

  const getSnapshot = useCallback(
    () =>
      window.localStorage.getItem(SIDEBAR_VISIBILITY_STORAGE_KEY) === "true",
    [],
  );
  const getServerSnapshot = useCallback(() => false, []);
  const isSidebarCollapsed = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const toggleSidebar = useCallback(() => {
    window.localStorage.setItem(
      SIDEBAR_VISIBILITY_STORAGE_KEY,
      String(!getSnapshot()),
    );
    window.dispatchEvent(new Event(SIDEBAR_VISIBILITY_EVENT));
  }, [getSnapshot]);

  const value = useMemo(
    () => ({ isSidebarCollapsed, toggleSidebar }),
    [isSidebarCollapsed, toggleSidebar],
  );

  return (
    <SidebarVisibilityContext.Provider value={value}>
      {children}
    </SidebarVisibilityContext.Provider>
  );
}

export function useOptionalSidebarVisibility() {
  return useContext(SidebarVisibilityContext);
}
